/** Phase 17 — Onboarding wizard route.
 *
 *  Layout: full-width shell (no sidebar). Left rail = StepperRail; right pane
 *  = the active StepShell. Fetches `/onboarding` + `/oauth/providers` on
 *  mount. Persists step transitions via PATCH.
 *
 *  All step bodies live inline as small function-components (kept short so
 *  the whole wizard reads top-to-bottom). i18n dict lives in
 *  `../../i18n/onboarding.js`.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, MinusCircle, AlertTriangle,
              ExternalLink, Loader2, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { ONBOARDING_STEPS, i18n as ONB_I18N, useLang, setLang }
    from "../../i18n/onboarding";
import DiagnosticsPanel from "../../components/app/DiagnosticsPanel";
import OAuthAccountSelection from "./OAuthAccountSelection";

const AD_PLATFORMS = ["ad_platform_meta", "ad_platform_google",
                             "ad_platform_linkedin", "ad_platform_tiktok"];
// UI screen order: intro + 11 backend steps + finish = 13 screens.
const SCREENS = ["intro", ...ONBOARDING_STEPS, "finish"];

function stepIcon(status) {
  if (status === "done")
    return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "in_progress")
    return <Loader2 className="h-4 w-4 text-blue-600" />;
  if (status === "skipped")
    return <MinusCircle className="h-4 w-4 text-slate-400" />;
  if (status === "blocked")
    return <AlertTriangle className="h-4 w-4 text-rose-600" />;
  return <Circle className="h-4 w-4 text-slate-300" />;
}

// ─── Language toggle (top-right of shell) ──────────────────────────────
function LanguageToggle({ lang, onChange }) {
  return (
    <div className="inline-flex rounded-md border overflow-hidden text-xs"
             data-testid="onboarding-language-toggle">
      {["de", "en"].map((l) => (
        <button
          key={l}
          onClick={() => onChange(l)}
          data-testid={`lang-${l}`}
          className={`px-2 py-1 ${lang === l
                ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ─── StepperRail ─────────────────────────────────────────────────────────
function StepperRail({ screens, activeIdx, state, onJump, lang }) {
  const t = ONB_I18N[lang];
  return (
    <aside className="w-64 shrink-0 space-y-1"
              data-testid="onboarding-stepper">
      {screens.map((key, idx) => {
        const s = state?.steps?.[key];
        const status = s?.status || (key === "intro" || key === "finish"
                                              ? "" : "pending");
        const label = key === "intro" ? (t.intro_short || t.intro_title)
                            : key === "finish" ? (t.finish_short || t.finish_title)
                            : t.steps[key]?.t || key;
        return (
          <button
            key={key}
            data-testid={`stepper-${key}`}
            onClick={() => onJump(idx)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition
              ${idx === activeIdx ? "bg-slate-900 text-white"
                : "hover:bg-slate-100"}`}>
            <span className="shrink-0">
              {key === "intro" || key === "finish"
                ? <Circle className="h-4 w-4 text-slate-300" />
                : stepIcon(status)}
            </span>
            <span className="truncate">{label}</span>
          </button>
        );
      })}
    </aside>
  );
}

// ─── Individual step bodies ─────────────────────────────────────────────
function IntroBody({ lang, onNext }) {
  const t = ONB_I18N[lang];
  return (
    <div data-testid="step-intro">
      <p className="text-sm text-muted-foreground mb-6">{t.intro_body}</p>
      <Button data-testid="intro-cta" onClick={onNext}>
        {t.intro_cta} <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

function GenericFormStep({ stepKey, lang, deepLinkTo, deepLinkLabel }) {
  const t = ONB_I18N[lang];
  return (
    <div data-testid={`step-${stepKey}`}>
      <p className="text-sm text-muted-foreground mb-4">
        {t.steps[stepKey]?.s}
      </p>
      {deepLinkTo && (
        <Button variant="outline" asChild data-testid={`deeplink-${stepKey}`}>
          <a href={deepLinkTo}>{deepLinkLabel || "Öffnen"}
             <ExternalLink className="ml-1 h-3 w-3" /></a>
        </Button>
      )}
    </div>
  );
}

function StepCRMViona({ lang, vionaStatus, onSaved }) {
  const t = ONB_I18N[lang];
  const [baseUrl, setBaseUrl] = useState(vionaStatus?.base_url || "");
  const [apiKey, setApiKey] = useState("");
  const [tenantScope, setTenantScope] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  async function submit() {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/integrations/crm/viona/configure",
        {base_url: baseUrl, api_key: apiKey,
          tenant_scope: tenantScope || undefined});
      const ok = r.data?.test_result?.ok !== false;
      setResult({ok, msg: r.data?.test_result?.error
                              || r.data?.test_result?.note || ""});
      if (ok) toast.success(t.viona.test_ok);
      else toast.error(t.viona.test_error + (r.data?.test_result?.error || ""));
      if (onSaved) onSaved();
    } catch (e) {
      setResult({ok: false,
                     msg: e?.response?.data?.detail?.error || String(e)});
      toast.error(t.viona.test_error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="step-crm_viona">
      {vionaStatus?.configured && (
        <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-md"
                data-testid="viona-already-configured">
          {t.viona.already_configured}
          <span className="font-mono">{vionaStatus.base_url}</span>
        </div>
      )}
      <div>
        <label className="text-xs text-muted-foreground">{t.viona.base_url}</label>
        <Input data-testid="viona-base-url" value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="mock://viona" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">{t.viona.api_key}</label>
        <Input data-testid="viona-api-key" value={apiKey} type="password"
                  onChange={(e) => setApiKey(e.target.value)} />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">
          {t.viona.tenant_scope}
        </label>
        <Input data-testid="viona-tenant-scope" value={tenantScope}
                  onChange={(e) => setTenantScope(e.target.value)} />
      </div>
      <Button data-testid="viona-submit" onClick={submit}
                 disabled={loading || !baseUrl || !apiKey}>
        {loading ? t.testing : (vionaStatus?.configured ? t.reconnect : t.connect)}
      </Button>
      {result && (
        <div className={`text-xs ${result.ok ? "text-emerald-700"
                                             : "text-rose-700"}`}
                data-testid="viona-result">
          {result.ok ? t.viona.test_ok : t.viona.test_error + (result.msg || "")}
        </div>
      )}
    </div>
  );
}

function StepAdPlatform({ platform, lang, provider, onRefresh }) {
  const t = ONB_I18N[lang];
  const stepKey = `ad_platform_${platform}`;
  const available = !!provider?.available;
  const connected = provider?.connection_status === "active";
  const authorizeUrl =
    `/api/v1/oauth/${platform}/authorize?return_to=/app/onboarding`;

  async function disconnect() {
    try {
      await api.post(`/oauth/${platform}/disconnect`);
      toast.success(t.disconnect);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(String(e));
    }
  }

  if (!available) {
    return (
      <div className="space-y-3" data-testid={`step-${stepKey}`}>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4"
                data-testid={`platform-unavailable-${platform}`}>
          <div className="flex items-center gap-2 font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            {t.provider_unavailable_title}
          </div>
          <p className="text-sm text-amber-900 mt-1">
            {t.provider_unavailable_body}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-md border p-4 opacity-70"
                data-testid={`platform-card-a-${platform}`}>
            <div className="font-medium text-sm">
              Plattform-Verbindung
              <span className="ml-2 text-xs text-muted-foreground">
                (in Vorbereitung)
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Der Plattform-Betreiber richtet gerade die zentrale
              Integration ein. Sobald verfügbar, verbindest du hier
              dein Konto mit einem Klick.
            </p>
          </div>
          <div className="rounded-md border p-4 border-emerald-300 bg-emerald-50/40"
                data-testid={`platform-card-b-${platform}`}>
            <div className="font-medium text-sm">
              Eigene App verwenden (empfohlen für Test-Setup)
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Registriere selbst eine Business-App bei
              {platform === "meta" ? " Facebook Developers"
                : platform === "google" ? " Google Cloud Console"
                : platform === "linkedin" ? " LinkedIn Developers"
                : " TikTok for Business"}
              , trage Client-ID + Secret ein und verbinde direkt —
              kein App-Review nötig.
            </p>
            <Button size="sm" variant="secondary" className="mt-3" asChild
                     data-testid={`platform-byoa-cta-${platform}`}>
              <a href="/app/integrations">
                Eigene App konfigurieren →
              </a>
            </Button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid={`platform-docs-${platform}`}>
            <a href={provider?.docs_url} target="_blank" rel="noreferrer">
              {t.docs} <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          </Button>
          <Button variant="outline" asChild
                     data-testid={`platform-support-${platform}`}>
            <a href="mailto:support@viona.immo">{t.provider_unavailable_support}</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`step-${stepKey}`}>
      {connected && provider?.provider_account_label && (
        <div className="text-sm bg-emerald-50 text-emerald-800 p-3 rounded-md"
                data-testid={`platform-connected-${platform}`}>
          {t.connected}: <b>{provider.provider_account_label}</b>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          data-testid={`platform-connect-${platform}`}
          onClick={() => { window.location.assign(authorizeUrl); }}>
          {connected ? t.reconnect : t.connect} — {provider?.display_name}
        </Button>
        {connected && (
          <Button variant="outline" onClick={disconnect}
                     data-testid={`platform-disconnect-${platform}`}>
            {t.disconnect}
          </Button>
        )}
      </div>
    </div>
  );
}

function StepSmtp({ lang, smtpHealthy }) {
  const t = ONB_I18N[lang];
  if (smtpHealthy) {
    return (
      <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-md"
              data-testid="step-smtp-enabled">
        {t.smtp_body_enabled}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="step-smtp-disabled">
      <p className="text-sm">{t.smtp_body_disabled}</p>
      <ul className="text-xs font-mono list-disc pl-4">
        <li>SMTP_ENABLED=true</li>
        <li>SMTP_HOST=…</li>
        <li>SMTP_PORT=587</li>
        <li>SMTP_USER=…</li>
        <li>SMTP_PASS=…</li>
        <li>SMTP_FROM=noreply@your-domain</li>
      </ul>
    </div>
  );
}

function StepAutopilot({ lang, value, onChange }) {
  const t = ONB_I18N[lang];
  const modes = ["prepare_only", "with_approval", "auto_within_frame"];
  return (
    <div className="space-y-2" data-testid="step-autopilot_mode">
      {modes.map((m) => (
        <label key={m}
                data-testid={`autopilot-${m}`}
                className={`flex gap-3 rounded-md border p-3 cursor-pointer
                             ${value === m ? "border-slate-900 bg-slate-50"
                                                : "hover:bg-slate-50"}`}>
          <input
            type="radio" name="autopilot"
            checked={value === m}
            onChange={() => onChange(m)}
            className="mt-1"
          />
          <div className="text-sm">
            <div className="font-medium">{t.autopilot[m].title}</div>
            <div className="text-muted-foreground">{t.autopilot[m].body}</div>
          </div>
        </label>
      ))}
      {value && value !== "prepare_only" && (
        <p className="text-xs text-amber-700" data-testid="autopilot-warning">
          <AlertTriangle className="inline h-3 w-3 mr-1" />
          {t.warn_autopilot}
        </p>
      )}
    </div>
  );
}

function FinishBody({ lang, state, onComplete }) {
  const t = ONB_I18N[lang];
  const rows = ONBOARDING_STEPS.map((k) => ({
    key: k, status: state?.steps?.[k]?.status || "pending",
  }));
  return (
    <div data-testid="step-finish">
      <p className="text-sm text-muted-foreground mb-4">{t.finish_body}</p>
      <ul className="space-y-1 mb-4">
        {rows.map((r) => (
          <li key={r.key} className="text-sm flex items-center gap-2">
            {stepIcon(r.status)}
            {ONB_I18N[lang].steps[r.key]?.t || r.key}
          </li>
        ))}
      </ul>
      <div className="mb-6">
        <DiagnosticsPanel embedded />
      </div>
      <Button data-testid="finish-cta" onClick={onComplete}>
        {t.to_dashboard} <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

// ─── Main route ─────────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate();
  const [lang, setLangState] = useState(useLang());
  const [state, setState] = useState(null);
  const [providers, setProviders] = useState([]);
  const [vionaStatus, setVionaStatus] = useState(null);
  const [smtpHealthy, setSmtpHealthy] = useState(false);
  const [autopilotMode, setAutopilotMode] = useState("prepare_only");
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  // Master-P3 Wave 8.3 — OAuth account-selection modal state.
  // Populated from URL query params (?needs_account_selection=1&
  // connection_id=…&platform=…) set by the callback redirect.
  const [oauthSelection, setOauthSelection] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("needs_account_selection") === "1") {
        return {platform: params.get("platform"),
                  connectionId: params.get("connection_id")};
      }
    } catch (_) { /* SSR / test env */ }
    return null;
  });

  const t = ONB_I18N[lang];
  const activeKey = SCREENS[activeIdx];

  async function refresh() {
    try {
      const [onb, prov, vs, health] = await Promise.all([
        api.get("/onboarding"),
        api.get("/oauth/providers"),
        api.get("/integrations/crm/viona/status").catch(() => ({data: {}})),
        api.get("/health/deep").catch(() => ({data: {}})),
      ]);
      setState(onb.data);
      setProviders(prov.data?.providers || []);
      setVionaStatus(vs.data || {});
      const smtp = (health.data?.checks || [])
        .find((c) => c.name === "smtp");
      setSmtpHealthy(smtp?.status === "ok");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  // Keyboard nav ←/→
  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  function next() { setActiveIdx((i) => Math.min(SCREENS.length - 1, i + 1)); }
  function back() { setActiveIdx((i) => Math.max(0, i - 1)); }

  async function patchStep(key, status) {
    if (!ONBOARDING_STEPS.includes(key)) return;
    try {
      const r = await api.patch(`/onboarding/step/${key}`, {status});
      setState(r.data);
    } catch (e) {
      toast.error("PATCH failed: " + String(e));
    }
  }

  async function handleContinue() {
    if (ONBOARDING_STEPS.includes(activeKey)) {
      await patchStep(activeKey, "done");
    }
    next();
  }

  async function handleSkip() {
    if (ONBOARDING_STEPS.includes(activeKey)) {
      await patchStep(activeKey, "skipped");
    }
    next();
  }

  async function handleComplete() {
    try { await api.post("/onboarding/complete"); }
    catch (_) { /* 409 acceptable — informational */ }
    navigate("/app");
  }

  function onLangChange(l) { setLangState(l); setLang(l); }

  const pct = state?.progress_pct ?? 0;
  const providerByPlatform = useMemo(() => {
    const m = {};
    for (const p of providers) m[p.platform] = p;
    return m;
  }, [providers]);

  // ─── Screen body switch ────────────────────────────────────────────────
  function renderBody() {
    if (activeKey === "intro") return <IntroBody lang={lang} onNext={next} />;
    if (activeKey === "finish")
      return <FinishBody lang={lang} state={state} onComplete={handleComplete} />;
    if (activeKey === "brand_profile")
      return (
        <div className="space-y-4">
          <div className="rounded-md border p-4 bg-emerald-50/30"
                data-testid="onboarding-branding-cta">
            <div className="text-sm font-medium">
              Automatisch aus deiner Website erstellen
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Wir crawlen deine Firmenwebsite und schlagen Logo,
              Farben, Ton, Zielgruppen, Regionen und USPs vor. Dauert
              ~30 Sekunden.
            </p>
            <Button size="sm" className="mt-2" asChild
                     data-testid="btn-onboarding-branding">
              <a href="/app/branding-scan">Website analysieren →</a>
            </Button>
          </div>
          <GenericFormStep stepKey="brand_profile" lang={lang}
                                deepLinkTo="/app/settings"
                                deepLinkLabel={lang === "de"
                                    ? "Zum Markenprofil" : "Open brand profile"} />
        </div>
      );
    if (activeKey === "regions")
      return <GenericFormStep stepKey="regions" lang={lang}
                                   deepLinkTo="/app/settings"
                                   deepLinkLabel={lang === "de"
                                       ? "Regionen verwalten" : "Manage regions"} />;
    if (activeKey === "target_groups")
      return <GenericFormStep stepKey="target_groups" lang={lang}
                                   deepLinkTo="/app/settings" />;
    if (activeKey === "products_services")
      return <GenericFormStep stepKey="products_services" lang={lang}
                                   deepLinkTo="/app/settings" />;
    if (activeKey === "crm_viona")
      return <StepCRMViona lang={lang} vionaStatus={vionaStatus}
                                onSaved={refresh} />;
    if (AD_PLATFORMS.includes(activeKey)) {
      const platform = activeKey.replace("ad_platform_", "");
      return <StepAdPlatform platform={platform} lang={lang}
                                  provider={providerByPlatform[platform]}
                                  onRefresh={refresh} />;
    }
    if (activeKey === "smtp")
      return <StepSmtp lang={lang} smtpHealthy={smtpHealthy} />;
    if (activeKey === "autopilot_mode")
      return <StepAutopilot lang={lang} value={autopilotMode}
                                 onChange={setAutopilotMode} />;
    return null;
  }

  const primaryLabel = activeKey === "intro" ? t.intro_cta
      : activeKey === "finish" ? t.to_dashboard : t.continue;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="onboarding-page">
      <header className="max-w-6xl mx-auto flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t.wizard_title}
          </div>
          <h1 className="text-xl font-semibold" data-testid="onboarding-title">
            VIONA Marketing OS
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle lang={lang} onChange={onLangChange} />
          <Button variant="ghost" onClick={() => navigate("/app")}
                     data-testid="onboarding-later">
            {t.later}
          </Button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto mb-4">
        <div className="flex items-center gap-3">
          <Progress value={pct} className="h-2"
                       data-testid="onboarding-progress" />
          <span className="text-xs font-mono text-muted-foreground">{pct}%</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto flex gap-6">
        <StepperRail screens={SCREENS} activeIdx={activeIdx}
                          state={state}
                          onJump={setActiveIdx} lang={lang} />
        <Card className="flex-1 min-h-[420px]" data-testid="step-shell">          <CardHeader>
            <CardTitle className="text-lg" data-testid="step-title">
              {activeKey === "intro"
                ? t.intro_title
                : activeKey === "finish"
                    ? t.finish_title
                    : t.steps[activeKey]?.t}
            </CardTitle>
            {activeKey !== "intro" && activeKey !== "finish" && (
              <p className="text-xs text-muted-foreground">
                {t.steps[activeKey]?.s}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {loading && activeKey !== "intro" && activeKey !== "finish"
              ? <div className="text-sm text-muted-foreground">Loading…</div>
              : renderBody()}
          </CardContent>
          {activeKey !== "intro" && activeKey !== "finish" && (
            <div className="flex items-center justify-between border-t p-4">
              <Button variant="outline" onClick={back}
                         data-testid="footer-back">{t.back}</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSkip}
                           data-testid="footer-skip">{t.skip}</Button>
                <Button onClick={handleContinue}
                           data-testid="footer-continue">
                  {primaryLabel}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      {oauthSelection && (
        <OAuthAccountSelection
          open={!!oauthSelection}
          platform={oauthSelection.platform}
          connectionId={oauthSelection.connectionId}
          lang={lang}
          onClose={() => {
            setOauthSelection(null);
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("needs_account_selection");
              url.searchParams.delete("connection_id");
              url.searchParams.delete("platform");
              window.history.replaceState({}, "", url.toString());
            } catch (_) { /* jsdom */ }
          }}
          onSuccess={() => { refresh(); }}
        />
      )}
    </div>
  );
}
